import { createTypeGuard, TypeGuardOf } from "../lib/create-type-guard.js";
import {
  checkCondition,
  Condition,
  ConditionData,
  isCondition,
} from "./condition.js";
import { Keyword } from "./keyword.js";
import { Unit } from "./unit.js";

/**
 * The order in which modifiers of a given type are applied. `SET` runs first so
 * that it acts as a new baseline other keywords can build on, whereas
 * `SET_FINAL` runs last, making its value impossible to modify further. The
 * latter is how keywords that "cannot be increased by any other keywords"
 * (Flame Weapons, Brawler) are expressed.
 *
 * `INCREMENT` and `MULTIPLY` share a rank, so — sorting being stable — they
 * apply in the order the ruleset lists them.
 */
const statModifierOrder: Record<StatModifierType, number> = {
  SET: 0,
  INCREMENT: 1,
  MULTIPLY: 1,
  SET_FINAL: 2,
};

function sortStatModifiers(a: StatModifier, b: StatModifier) {
  return statModifierOrder[a.type] - statModifierOrder[b.type];
}

export function applyModifiers(
  unit: Unit,
  modifiers: StatModifier[],
  initialValue: number,
) {
  return modifiers
    .slice()
    .sort(sortStatModifiers)
    .filter(filterModifierByCondition(unit))
    .reduce(reduceModifier, initialValue);
}

function filterModifierByCondition(unit: Unit) {
  return (modifier: StatModifier) => {
    if (!modifier.conditions) {
      // If no conditions are specified we always accept that modifier
      return true;
    }

    return modifier.conditions.some(checkCondition(modifier.keyword.id, unit));
  };
}

function reduceModifier(accumulator: number, effect: StatModifier) {
  switch (effect.type) {
    case "INCREMENT": {
      return accumulator + effect.value;
    }

    case "MULTIPLY": {
      return Math.floor(accumulator * effect.value);
    }

    case "SET":
    case "SET_FINAL": {
      return effect.value;
    }

    // Unknown type is a noop.
    default: {
      return accumulator;
    }
  }
}

export type StatModifierData = {
  type: string;
  stat: string;
  value: number;
  conditions?: ConditionData[];
};

export type StatModifierType = TypeGuardOf<typeof isStatModifierType>;

const isStatModifierType = createTypeGuard([
  "INCREMENT",
  "MULTIPLY",
  "SET",
  "SET_FINAL",
]);

export type StatModifierTargetStat = TypeGuardOf<
  typeof isStatModifierTargetStat
>;

const isStatModifierTargetStat = createTypeGuard([
  "movement",
  "defense",
  "morale",
  "range",

  // `combat` applies to both ranged and melee attacks, whereas
  // `ranged_combat` and `melee_combat` apply on top of it to one only.
  "combat",
  "ranged_combat",
  "melee_combat",
]);

export class StatModifier {
  keyword: Keyword;
  type: StatModifierType;
  stat: StatModifierTargetStat;
  value: number;
  conditions: Condition[] | null;

  constructor(
    keyword: Keyword,
    type: StatModifierType,
    stat: StatModifierTargetStat,
    value: number,
    conditions?: ConditionData[],
  ) {
    this.keyword = keyword;
    this.type = type;
    this.stat = stat;
    this.value = value;
    this.conditions = conditions?.filter(isCondition) ?? null;
  }

  static from(keyword: Keyword, data: StatModifierData) {
    if (isStatModifierType(data.type) && isStatModifierTargetStat(data.stat)) {
      return new StatModifier(
        keyword,
        data.type,
        data.stat,
        data.value,
        data.conditions,
      );
    }

    return null;
  }
}
